/*global navigator*/
import {observable, action} from 'mobx';
import {lazyInitialize, nonenumerable} from 'spa/decorators';

import BasketsState from './basket-new';
import BasketState from './basket';
import createSdkInstance from '../sdk';
import createIterableSdkInstance from '../iterable-sdk';
import createCmsSdkInstance from '../cms-sdk';
import ApplePayState from './_util/apple-pay';
import IterableState from './_util/iterable';
import CategoriesState from './categories';
import ConfigState from './config';
import ContentState from './cms-content';
import I18nState from './i18n';
import LocalStorageHelperState from './localStorageHelper';
import ScreenState from './screen';
import SpecialitiesState from './specialities';
import SpecialitiesFAQState from './specialities-faq';
import SpecialityByStubState from './speciality-by-stub';
import SpecialityGetAvailabilityState from './speciality-availability';
import WorkersState from './workers';
import WorkersStateV2 from './workers-v2';
import WorkersLanguagesState from './worker-languages';
import SessionState from './session';
import NotificationsState from './notifications';
import BookingsState from './bookings';
import OrdersState from './orders';
import OrdersCalculateUpdateState from './orders-calculate-update';
import AddressState from './address';
import BraintreeState from './payments/braintree';
import ClassSelectionState from './class-selection';
import SelectionState from './selection';
import CitiesState from './cities';
import CitiesApiState from './cities-api';
import CreditState from './credit';
import CountriesState from './countries';
import EcommerceState from './ecommerce';
import PromoState from './promo-pages';
import ProductsState from './products';
import ResetPasswordState from './reset-password';
import SchemesState from './schemes';
import HomeState from './home';
import UserPaymentMethodsState from './user/payment-methods';
import BillingState from './payments/billing';
import UpdateSearchAttemptState from './update-search-attempt';
import CancellationPolicyState from './cancellation-policy';
import Tracker from './tracker';
import CMSCategoriesState from './cms/cms-categories';
import CMSLocationsState from './cms/cms-locations';
import CMSPressState from './cms/cms-press';
import CMSPostsState from './cms/cms-posts';
import UserReferralState from './user-referral';
import PartnerOffersState from './partner-offers';
import RedeemReferralState from './redeem-referral';
import SubscribeState from './subscribe';
import UnsubscribeState from './unsubscribe';
import UserAddressesState from './user/user-addresses';
import UserGetPhoneStatusState from './user/user-phone-status';
import UserInviteState from './user/user-referral-invites';
import UserPartnerAccountsState from './user/user-partner-accounts';
import UserSchemeState from './user/user-referral-scheme';
import UserSetPhoneState from './user/user-phone-set';
import UserSubscriptionsState from './user/user-subscriptions';
import UpdateUserProfileState from './user/update-profile';
import VouchersState from './vouchers';
import GiftsState from './gifts';
import LookupEmailState from './lookup-email';
import CurrenciesState from './currencies';
import RatingState from './rating';
import SearchAttemptsState from './search-attempts';
import TippingState from './tipping';
import CampaignsState from './campaigns';
import VerifyAccountState from './verify-account';
import VerticalsConfigState from './verticals-config';
import WorkerPortfolioState from './worker-portfolio';
import WorkerReviewsState from './worker-reviews';
import WorkerPromotedReviewsState from './worker-promoted-reviews';
import DatesState from './dates';
import SlotsState from './slots';
import WorkerFavouriteState from './worker-favourite';
import AppointmentsRecommendationState from './appointments-recommendation';
import NavigationState from './navigation';
import ProductsStateV2 from './productsV2';
import CurrencyStateV2 from './currenciesV2';
import LoyaltyState from './loyalty';
import DeviceDetection from './deviceDetection';

const isServer = typeof window === 'undefined';

class AppState {
  session = new SessionState(this);

  @lazyInitialize @nonenumerable sdk = createSdkInstance(this);
  @lazyInitialize @nonenumerable iterableSDK = createIterableSdkInstance(this);
  @lazyInitialize @nonenumerable cmsSdk = createCmsSdkInstance(this);

  @nonenumerable @lazyInitialize screen = new ScreenState();
  @nonenumerable @lazyInitialize i18n = new I18nState(this);
  @nonenumerable @lazyInitialize localStorageHelper = new LocalStorageHelperState(this);
  @nonenumerable @lazyInitialize config = new ConfigState(this);
  @nonenumerable @lazyInitialize content = new ContentState(this);
  @nonenumerable @lazyInitialize tracker = new Tracker(this);

  @observable anonymousId;
  @observable userAgent;
  @observable initialLoadComplete = false;
  @observable umDeviceIdentifier;
  @observable utmCookie;

  @nonenumerable @lazyInitialize applePay = new ApplePayState(this);
  @nonenumerable @lazyInitialize basket = new BasketState(this);
  // TODO Rename to basket once we remove previous basket state
  @nonenumerable @lazyInitialize basketNew = new BasketsState(this);
  @nonenumerable @lazyInitialize searchAttempt = new SearchAttemptsState(this);

  @nonenumerable @lazyInitialize iterable = new IterableState(this);

  @nonenumerable @lazyInitialize specialities = new SpecialitiesState(this);
  @nonenumerable @lazyInitialize specialitiesFaq = new SpecialitiesFAQState(this);
  @nonenumerable @lazyInitialize specialityByStub = new SpecialityByStubState(this);
  @nonenumerable @lazyInitialize specialityAvailability = new SpecialityGetAvailabilityState(this);

  @nonenumerable @lazyInitialize workers = new WorkersState(this);
  @nonenumerable @lazyInitialize workersV2 = new WorkersStateV2(this);
  @nonenumerable @lazyInitialize workerLanguages = new WorkersLanguagesState(this);
  @nonenumerable @lazyInitialize notifications = new NotificationsState(this);
  @nonenumerable @lazyInitialize bookings = new BookingsState(this);
  @nonenumerable @lazyInitialize orders = new OrdersState(this);
  @nonenumerable @lazyInitialize ordersCalculateUpdate = new OrdersCalculateUpdateState(this);
  @nonenumerable @lazyInitialize address = new AddressState(this);
  @nonenumerable @lazyInitialize braintree = new BraintreeState(this);
  @nonenumerable @lazyInitialize classSelection = new ClassSelectionState(this);
  @nonenumerable @lazyInitialize selection = new SelectionState(this);
  @nonenumerable @lazyInitialize cancellationPolicy = new CancellationPolicyState(this);

  @nonenumerable @lazyInitialize cities = new CitiesState(this);
  @nonenumerable @lazyInitialize citiesApi = new CitiesApiState(this);
  @nonenumerable @lazyInitialize countries = new CountriesState(this);
  @nonenumerable @lazyInitialize credit = new CreditState(this);
  @nonenumerable @lazyInitialize currencies = new CurrenciesState(this);
  @nonenumerable @lazyInitialize ecommerce = new EcommerceState(this);
  @nonenumerable @lazyInitialize gifts = new GiftsState(this);
  @nonenumerable @lazyInitialize home = new HomeState(this);
  @nonenumerable @lazyInitialize lookupEmail = new LookupEmailState(this);
  @nonenumerable @lazyInitialize partnerAccounts = new UserPartnerAccountsState(this);
  @nonenumerable @lazyInitialize partnerOffers = new PartnerOffersState(this);
  @nonenumerable @lazyInitialize products = new ProductsState(this);
  @nonenumerable @lazyInitialize promo = new PromoState(this);
  @nonenumerable @lazyInitialize redeemReferral = new RedeemReferralState(this);
  @nonenumerable @lazyInitialize resetPassword = new ResetPasswordState(this);
  @nonenumerable @lazyInitialize schemes = new SchemesState(this);
  @nonenumerable @lazyInitialize subscribe = new SubscribeState(this);
  @nonenumerable @lazyInitialize userPaymentMethods = new UserPaymentMethodsState(this);
  @nonenumerable @lazyInitialize billing = new BillingState(this);
  @nonenumerable @lazyInitialize updateSearchAttempt = new UpdateSearchAttemptState(this);
  @nonenumerable @lazyInitialize userReferral = new UserReferralState(this);
  @nonenumerable @lazyInitialize unsubscribe = new UnsubscribeState(this);
  @nonenumerable @lazyInitialize userAddresses = new UserAddressesState(this);
  @nonenumerable @lazyInitialize userInvites = new UserInviteState(this);
  @nonenumerable @lazyInitialize userScheme = new UserSchemeState(this);
  @nonenumerable @lazyInitialize userSubscriptions = new UserSubscriptionsState(this);
  @nonenumerable @lazyInitialize updateProfile = new UpdateUserProfileState(this);
  @nonenumerable @lazyInitialize vouchers = new VouchersState(this);
  @nonenumerable @lazyInitialize rating = new RatingState(this);
  @nonenumerable @lazyInitialize tipping = new TippingState(this);
  @nonenumerable @lazyInitialize campaigns = new CampaignsState(this);
  @nonenumerable @lazyInitialize verticalsConfig = new VerticalsConfigState(this);
  @nonenumerable @lazyInitialize categories = new CategoriesState(this);
  @nonenumerable @lazyInitialize verifyAccount = new VerifyAccountState(this);
  @nonenumerable @lazyInitialize workerPortfolio = new WorkerPortfolioState(this);
  @nonenumerable @lazyInitialize workerReviews = new WorkerReviewsState(this);
  @nonenumerable @lazyInitialize workerPromotedReviews = new WorkerPromotedReviewsState(this);
  @nonenumerable @lazyInitialize dates = new DatesState(this);
  @nonenumerable @lazyInitialize slots = new SlotsState(this);
  @nonenumerable @lazyInitialize userGetPhoneStatus = new UserGetPhoneStatusState(this);
  @nonenumerable @lazyInitialize userSetPhone = new UserSetPhoneState(this);
  @nonenumerable @lazyInitialize workerFavourite = new WorkerFavouriteState(this);
  @nonenumerable @lazyInitialize appointmentsRecommendation = new AppointmentsRecommendationState(this);
  @nonenumerable @lazyInitialize navigation = new NavigationState(this);
  @nonenumerable @lazyInitialize productsV2 = new ProductsStateV2(this);
  @nonenumerable @lazyInitialize currencySymbolV2 = new CurrencyStateV2(this);
  @nonenumerable @lazyInitialize loyaltyState = new LoyaltyState(this);
  @nonenumerable @lazyInitialize deviceDetection = new DeviceDetection(this);

  /* ===================
   * CMS States
   * =================== */

   @nonenumerable @lazyInitialize cmsCategories = new CMSCategoriesState(this);
   @nonenumerable @lazyInitialize cmsLocations = new CMSLocationsState(this);
   @nonenumerable @lazyInitialize cmsPress = new CMSPressState(this);
   @nonenumerable @lazyInitialize cmsPosts = new CMSPostsState(this);


  @action setup() {
     if (!isServer && !this.userAgent) { this.userAgent = navigator.userAgent; }
     this.i18n.setup();
     this.config.setup();
     this.content.setup();
   }

  dispose() {
    for (const key of Object.keys(this)) {
      if (this[key] && this[key].dispose) {
        this[key].dispose();
      }
    }
  }
}

export default AppState;



// WEBPACK FOOTER //
// ./spa/state/app.jsx